// ---------------------------------------------------------------------------
// Shared math utilities — isomorphic (server + client).
// World convention: y-up. Table surface at y = 0, cards/tokens lie on x/z.
// ---------------------------------------------------------------------------

export type V3 = { x: number; y: number; z: number };

export const v3 = (x = 0, y = 0, z = 0): V3 => ({ x, y, z });
export const vAdd = (a: V3, b: V3): V3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const vSub = (a: V3, b: V3): V3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const vScale = (a: V3, s: number): V3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const vLen = (a: V3): number => Math.hypot(a.x, a.y, a.z);
export const vDistXZ = (a: V3, b: V3): number => Math.hypot(a.x - b.x, a.z - b.z);

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const lerpV3 = (a: V3, b: V3, t: number): V3 => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  z: lerp(a.z, b.z, t),
});

/** Interpolate angles along the shortest arc (radians). */
export function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export const TAU = Math.PI * 2;

/** Normalize an angle to (-PI, PI]. */
export function normAngle(a: number): number {
  a = a % TAU;
  if (a > Math.PI) a -= TAU;
  if (a <= -Math.PI) a += TAU;
  return a;
}

/** Snap an angle to the nearest multiple of `step` radians. */
export const snapAngle = (a: number, step: number): number =>
  Math.round(a / step) * step;

/** Point-in-rotated-rect test on the XZ plane. Rect centered at (cx,cz), extents w×d, rotated by yaw. */
export function pointInRectXZ(
  px: number, pz: number,
  cx: number, cz: number,
  w: number, d: number, yaw: number,
): boolean {
  const cos = Math.cos(-yaw), sin = Math.sin(-yaw);
  const dx = px - cx, dz = pz - cz;
  const lx = dx * cos - dz * sin;
  const lz = dx * sin + dz * cos;
  return Math.abs(lx) <= w / 2 && Math.abs(lz) <= d / 2;
}

// ---------------------------------------------------------------------------
// Deterministic seeded RNG (mulberry32) — used server-side for shuffles and
// dice so outcomes are authoritative and replayable.
// ---------------------------------------------------------------------------

export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates shuffle in place using the provided RNG. */
export function shuffleInPlace<T>(arr: T[], rng: Rng): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

/** Short unique-ish id (object ids, room ids). */
export function shortId(rng?: Rng): string {
  const r = rng ?? Math.random;
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 8; i++) s += alphabet[Math.floor(r() * alphabet.length)];
  return s;
}
